import { Injectable } from '@angular/core';
import { OrganizationQuery } from '@core/organization/organization.query';
import { OrganizationType } from '@core/models/organization.model';
import { AppStoreQuery } from '@core/state';
import { UserProfile } from '@core/models/user-profile.model';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { DdqInvitationDirection } from '@core/models/ddq-invitation.model';
import { DdqInvitationService } from './ddq-invitation.service';
import { DdqInvitationStore } from './ddq-invitation.store';
import { DdqInvitationQuery } from './ddq-invitation.query';

@Injectable({ providedIn: 'root' })
export class DdqInvitationNavService {
  constructor(
    private readonly invitationService: DdqInvitationService,
    private readonly invitationStore: DdqInvitationStore,
    private readonly invitationQuery: DdqInvitationQuery,
    private readonly organizationQuery: OrganizationQuery,
    private readonly appStoreQuery: AppStoreQuery,
  ) {}

  /**
   * Whether the current user can use cross-organisation admin list endpoints.
   * Partner admins are excluded — admin invitation APIs are staff/superuser only.
   */
  isPrivilegedViewer(profile: UserProfile | null | undefined): boolean {
    return !!profile?.isStaff || !!profile?.isSuperUser;
  }

  /**
   * GP issuers list GP→LP invitations; LP investors and DDQ clients list LP→GP invitations.
   */
  resolveInviterDirection(
    organizationType: OrganizationType | null | undefined,
    isClient: boolean,
  ): DdqInvitationDirection {
    if (isClient) {
      return 'lp';
    }
    return organizationType === 'emittent' ? 'gp' : 'lp';
  }

  selectInviterDirection(): Observable<DdqInvitationDirection> {
    return combineLatest([
      this.organizationQuery.selectChosenOrganizationType(),
      this.appStoreQuery.selectUser().pipe(map((u) => !!u?.isClient)),
    ]).pipe(
      map(([orgType, isClient]) => this.resolveInviterDirection(orgType, isClient)),
      distinctUntilChanged(),
    );
  }

  /**
   * Re-check whether the user has sent any invitations (sidebar visibility).
   */
  refreshHasInvitees(): Observable<boolean> {
    return this.selectInviterDirection().pipe(
      switchMap((direction) => {
        const request$ =
          direction === 'gp'
            ? this.invitationService.listGpInvitations(
                'mine',
                { page: 1, pageSize: 1 },
                undefined,
                { updateStore: false, suppressLoading: true },
              )
            : this.invitationService.listLpInvitations(
                'mine',
                { page: 1, pageSize: 1 },
                undefined,
                { updateStore: false, suppressLoading: true },
              );

        return request$.pipe(
          map((res) => (res.pagination.total ?? 0) > 0),
          catchError(() => of(false)),
        );
      }),
      tap((hasInvitees) => this.invitationStore.setHasInvitees(hasInvitees)),
    );
  }

  selectHasInvitees(): Observable<boolean> {
    return this.invitationQuery.selectHasInvitees().pipe(
      distinctUntilChanged(),
      switchMap((cached) => {
        if (cached === null) {
          return this.refreshHasInvitees();
        }
        return of(cached);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }
}
